import { getConsent } from "@/consent/consent";
import { readAcquisition, UTM_KEYS } from "@/utils/acquisition";

/** One attribute, so an order's Additional details gets one line. */
export const ATTRIBUTION_ATTRIBUTE_KEY = "_attribution";

/** Each ad platform's click cookies; a new platform is one more entry. */
const AD_CLICK_COOKIES = {
  oppref: "__oppref",
  obref: "__obref",
  twclid: "_twclid",
  twpid: "_twpid",
} as const;

export type AdClickKey = keyof typeof AD_CLICK_COOKIES;

const AD_CLICK_KEYS = Object.keys(AD_CLICK_COOKIES) as AdClickKey[];

const CONSENT_FIELD = "marketing_consent";

const ATTRIBUTION_FIELDS = [
  ...UTM_KEYS,
  "referrer",
  "landing_page",
  "ad_click",
  CONSENT_FIELD,
  ...AD_CLICK_KEYS,
] as const;

type AttributionField = (typeof ATTRIBUTION_FIELDS)[number];

export type Attribution = Partial<Record<AttributionField, string>>;

export type AdClicks = Partial<Record<AdClickKey, string>>;

// Anyone can write cart attributes through the Storefront API, so values read
// back off an order are capped before they go upstream.
const MAX_VALUE_LENGTH = 512;

const readCookie = (name: string): string | undefined => {
  const prefix = `${name}=`;
  const entry = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(prefix));
  if (entry === undefined) return undefined;
  const value = decodeURIComponent(entry.slice(prefix.length));
  return value === "" ? undefined : value;
};

// The domain is enough to name the source; the full URL can carry a search.
const referrerDomain = (referrer: string | undefined): string | undefined => {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).hostname.replace(/^www\./, "");
  } catch (error) {
    console.error("Failed to read the referrer:", error);
    return undefined;
  }
};

// X's pixel stores the click as JSON alongside its own bookkeeping; the
// other platforms' cookies are the bare click reference.
const cookieClick = (key: string, raw: string): string | undefined => {
  if (key !== "twclid") return raw;
  try {
    const { twclid } = JSON.parse(raw) as { twclid?: unknown };
    return typeof twclid === "string" && twclid !== "" ? twclid : undefined;
  } catch (error) {
    console.error("Failed to read the X click cookie:", error);
    return undefined;
  }
};

/**
 * Where the purchase visit came from, and the ad-click references conversion
 * senders need. Empty without marketing consent, which tells the server to
 * send nothing.
 */
export const checkoutAttribution = (): Attribution => {
  if (!getConsent().marketing) return {};
  const visit = readAcquisition();
  const attribution: Attribution = { [CONSENT_FIELD]: "granted" };
  for (const key of UTM_KEYS) {
    if (visit[key]) attribution[key] = visit[key];
  }
  const referrer = referrerDomain(visit.referrer);
  if (referrer) attribution.referrer = referrer;
  if (visit.landing_page) attribution.landing_page = visit.landing_page;
  if (visit.ad_click) attribution.ad_click = visit.ad_click;
  for (const [key, cookie] of Object.entries(AD_CLICK_COOKIES)) {
    const raw = readCookie(cookie);
    const value = raw === undefined ? undefined : cookieClick(key, raw);
    if (value !== undefined) attribution[key as AdClickKey] = value;
  }
  return attribution;
};

export const serializeAttribution = (
  attribution: Attribution,
): string | null =>
  Object.keys(attribution).length === 0 ? null : JSON.stringify(attribution);

export const parseAttribution = (
  value: string | null | undefined,
): Attribution => {
  if (!value) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null) return {};
    const attribution: Attribution = {};
    for (const field of ATTRIBUTION_FIELDS) {
      const raw = (parsed as Record<string, unknown>)[field];
      if (typeof raw === "string" && raw !== "") {
        attribution[field] = raw.slice(0, MAX_VALUE_LENGTH);
      }
    }
    return attribution;
  } catch (error) {
    console.error("Failed to parse the attribution attribute:", error);
    return {};
  }
};

export const adClicksFrom = (attribution: Attribution): AdClicks | null => {
  if (attribution[CONSENT_FIELD] !== "granted") return null;
  const clicks: AdClicks = {};
  for (const key of AD_CLICK_KEYS) {
    const value = attribution[key];
    if (value !== undefined) clicks[key] = value;
  }
  return clicks;
};
